import React, { useCallback, useMemo } from 'react';

import { Input } from '@appcharge/shared-ui';

import { IJSONSchemaProperty, SchemaType } from '../../../integration-test-modal.types';
import { schemaUtils } from '../../../integration-test-modal.utils';

const NULL_DISPLAY_VALUE = 'null';

const DEFAULT_VALUES_BY_TYPE: Record<SchemaType, unknown> = {
  [SchemaType.STRING]: '',
  [SchemaType.NUMBER]: 0,
  [SchemaType.INTEGER]: 0,
  [SchemaType.BOOLEAN]: false,
  [SchemaType.ARRAY]: [],
  [SchemaType.OBJECT]: {}
};

const FALLBACK_DEFAULT_VALUE = '';

const getDefaultValueForType = (type?: SchemaType): unknown => {
  if (!type) {
    return FALLBACK_DEFAULT_VALUE;
  }
  return DEFAULT_VALUES_BY_TYPE[type] ?? FALLBACK_DEFAULT_VALUE;
};

interface INullFieldDisplayProps {
  name: string;
  path: string;
  property: IJSONSchemaProperty;
  required?: boolean;
  onChange: (path: string, value: unknown) => void;
}

export const NullFieldDisplay: React.FC<INullFieldDisplayProps> = ({
  name,
  path,
  property,
  required,
  onChange
}) => {
  const fieldLabel = useMemo(
    () => schemaUtils.formatFieldLabel(name, property.title),
    [name, property.title]
  );

  const handleSetValue = useCallback((): void => {
    onChange(path, getDefaultValueForType(property.type));
  }, [path, onChange, property.type]);

  return (
    <div className="relative">
      <Input
        type="text"
        label={fieldLabel}
        isRequired={required}
        description={property.description}
        value={NULL_DISPLAY_VALUE}
        readOnly
        disabled
        className="italic text-base-mutedForeground"
      />
      <button
        type="button"
        onClick={handleSetValue}
        className="absolute right-2 top-[34px] rounded px-1.5 py-0.5 text-p-xs font-medium text-base-accentForeground hover:bg-base-border transition-colors"
      >
        Set value
      </button>
    </div>
  );
};
